import React from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import { formatPrice } from "src/lib/formatters";

type Product = {
	description: string;
	price: number;
};

interface ManualCheckoutSummaryProps {
	products: Product[];
	summary: {
		partner_fee: number;
		vat: number;
	};
	className?: string;
	variant: "light" | "dark";
}

const ManualCheckoutSummary = ({
	products,
	summary,
	className,
	variant,
}: ManualCheckoutSummaryProps) => {
	const { t } = useTranslation("payments", {
		keyPrefix: "checkout-summary",
	});
	const subtotal =
		products.reduce((value, item) => value + item.price, 0) +
		(summary.partner_fee || 0);
	const vat = subtotal * (summary.vat || 0);
	const total = subtotal + vat;

	return (
		<div className={`flex flex-col ${className || ""}`}>
			{products.map((item, index) => (
				<RowItem key={`description-${index}`} {...item} />
			))}
			{summary.partner_fee && (
				<RowItem
					description={t("partner-fee")}
					price={summary.partner_fee}
					help={t("partner-fee-disclaimer")}
				/>
			)}
			<hr
				className={`border-${
					variant === "dark" ? "dark-accent" : "gray"
				} my-2`}
			/>
			<RowItem description={t("subtotal")} price={subtotal} />
			<RowItem
				description={t("vat", {
					vat: summary.vat * 100,
				})}
				price={vat}
			/>
			<hr
				className={`border-${
					variant === "dark" ? "dark-accent" : "gray"
				} my-2`}
			/>
			<RowItem variant="strong" description={t("total")} price={total} />
		</div>
	);
};

ManualCheckoutSummary.defaultProps = {
	variant: "dark",
};

interface RowItemProps {
	description: string;
	price: number;
	help?: string;
	variant?: "strong";
}

const RowItem = ({ variant, description, price, help }: RowItemProps) => {
	return (
		<div className="flex items-center gap-2">
			<span className="flex flex-1">
				{variant === "strong" ? (
					<strong>{description}</strong>
				) : (
					description
				)}
				{help && (
					<span className="ml-2">
						<i
							data-tip
							data-for={`tooltip-${description}`}
							className="far fa-info-square opacity-20"
						></i>
						<ReactTooltip
							id={`tooltip-${description}`}
							effect="solid"
						>
							{help}
						</ReactTooltip>
					</span>
				)}
			</span>
			<span>{formatPrice(price)}</span>
		</div>
	);
};

export default ManualCheckoutSummary;
